import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Users, Edit2, Key, Plus, Trash2, Search, Building2 } from 'lucide-react';
import { toast } from 'sonner';
import apiClient from '@/services/api';

const SuperAdminUserManagement = () => {
  const [users, setUsers] = useState([]);
  const [stores, setStores] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterRole, setFilterRole] = useState('ALL');
  const [filterStore, setFilterStore] = useState('ALL');
  const [showEditModal, setShowEditModal] = useState(false);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const [editForm, setEditForm] = useState({ name: '', email: '', phone: '' });
  const [createForm, setCreateForm] = useState({ 
    name: '', 
    email: '', 
    phone: '', 
    password: '',
    role: 'POS',
    store_id: ''
  });

  useEffect(() => {
    loadStores();
    loadAllUsers();
  }, []);

  const loadStores = async () => {
    try {
      const response = await apiClient.get('/stores');
      setStores(response.data || []);
    } catch (error) {
      console.error('Error loading stores:', error);
    }
  };

  const loadAllUsers = async () => {
    setLoading(true);
    try {
      // Load users from all stores
      const response = await apiClient.get('/auth/all-users');
      setUsers(response.data || []);
    } catch (error) {
      console.error('Error loading users:', error);
      toast.error('Failed to load users');
    } finally {
      setLoading(false);
    }
  };

  const handleEdit = (user) => {
    setSelectedUser(user);
    setEditForm({
      name: user.name,
      email: user.email,
      phone: user.phone || ''
    });
    setShowEditModal(true);
  };

  const handleSaveEdit = async () => {
    try {
      await apiClient.put(`/auth/users/${selectedUser.id}`, editForm);
      toast.success('User updated successfully!');
      setShowEditModal(false);
      loadAllUsers();
    } catch (error) {
      console.error('Error updating user:', error);
      let errorMessage = 'Failed to update user';
      if (error.response?.data?.detail) {
        const detail = error.response.data.detail;
        if (typeof detail === 'string') {
          errorMessage = detail;
        } else if (Array.isArray(detail)) {
          errorMessage = detail.map(e => e.msg || e.message || JSON.stringify(e)).join(', ');
        }
      }
      toast.error(errorMessage);
    }
  };

  const handleCreateUser = async () => {
    if (!createForm.name || !createForm.email || !createForm.password) {
      toast.error('Please fill in all required fields');
      return;
    }

    if (createForm.role !== 'SUPER_ADMIN' && !createForm.store_id) {
      toast.error('Please select a store for this user');
      return;
    }

    try {
      const payload = { ...createForm };
      // For SUPER_ADMIN, store_id is not required
      if (payload.role === 'SUPER_ADMIN') {
        delete payload.store_id;
      }
      
      await apiClient.post('/auth/register', payload);
      toast.success('User created successfully!');
      setShowCreateModal(false);
      setCreateForm({ 
        name: '', 
        email: '', 
        phone: '', 
        password: '',
        role: 'POS',
        store_id: ''
      });
      loadAllUsers();
    } catch (error) {
      console.error('Error creating user:', error);
      let errorMessage = 'Failed to create user';
      if (error.response?.data?.detail) {
        const detail = error.response.data.detail;
        if (typeof detail === 'string') {
          errorMessage = detail;
        } else if (Array.isArray(detail)) {
          // Pydantic validation errors come as an array
          errorMessage = detail.map(e => e.msg || e.message || JSON.stringify(e)).join(', ');
        }
      }
      toast.error(errorMessage);
    }
  };

  const handleDeleteUser = async (user) => {
    if (user.role === 'SUPER_ADMIN') {
      toast.error('Cannot delete Super Admin users');
      return;
    }

    if (!confirm(`Are you sure you want to delete ${user.name}?\n\nThis action cannot be undone.`)) {
      return;
    }

    try {
      await apiClient.delete(`/auth/users/${user.id}`);
      toast.success('User deleted successfully!');
      loadAllUsers();
    } catch (error) {
      console.error('Error deleting user:', error);
      let errorMessage = 'Failed to delete user';
      if (error.response?.data?.detail) {
        const detail = error.response.data.detail;
        if (typeof detail === 'string') {
          errorMessage = detail;
        } else if (Array.isArray(detail)) {
          errorMessage = detail.map(e => e.msg || e.message || JSON.stringify(e)).join(', ');
        }
      }
      toast.error(errorMessage);
    }
  };

  const handleResetPassword = async (user) => {
    if (!confirm(`Reset password for ${user.name}?\n\nA new password will be generated.`)) {
      return;
    }

    try {
      const response = await apiClient.post(`/auth/reset-user-password/${user.id}`);
      alert(`Password Reset Successful!\n\nNew Password: ${response.data.new_password}\n\nPlease share this with ${user.name}.`);
      toast.success('Password reset successfully!');
    } catch (error) {
      console.error('Error resetting password:', error);
      let errorMessage = 'Failed to reset password';
      if (error.response?.data?.detail) {
        const detail = error.response.data.detail;
        if (typeof detail === 'string') {
          errorMessage = detail;
        } else if (Array.isArray(detail)) {
          errorMessage = detail.map(e => e.msg || e.message || JSON.stringify(e)).join(', ');
        }
      }
      toast.error(errorMessage);
    }
  };

  const getRoleBadge = (role) => {
    const colors = {
      'SUPER_ADMIN': 'bg-red-100 text-red-800 border border-red-300',
      'ADMIN': 'bg-purple-100 text-purple-800',
      'POS': 'bg-blue-100 text-blue-800',
      'KITCHEN': 'bg-orange-100 text-orange-800',
      'RIDER': 'bg-green-100 text-green-800',
      'KIOSK': 'bg-yellow-100 text-yellow-800'
    };
    return colors[role] || 'bg-gray-100 text-gray-800';
  };

  const getStoreName = (storeId) => {
    const store = stores.find(s => s.id === storeId);
    return store ? store.name : 'No Store';
  };

  // Filter users
  const filteredUsers = users.filter(user => {
    const matchesSearch = 
      user.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      user.email.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesRole = filterRole === 'ALL' || user.role === filterRole;
    const matchesStore = filterStore === 'ALL' || user.store_id === filterStore;
    return matchesSearch && matchesRole && matchesStore;
  });

  return (
    <div>
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 mb-6">
        <div>
          <h2 className="text-2xl font-bold">User Management</h2>
          <p className="text-gray-600 mt-1">Manage all users across all stores</p>
        </div>
        <Button onClick={() => setShowCreateModal(true)} className="bg-green-600 hover:bg-green-700">
          <Plus className="w-4 h-4 mr-2" />
          Create New User
        </Button>
      </div>

      {/* Filters */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-6">
        <div className="relative">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
          <Input
            placeholder="Search by name or email..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="pl-10"
          />
        </div>
        <Select value={filterRole} onValueChange={setFilterRole}>
          <SelectTrigger>
            <SelectValue placeholder="Filter by role" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="ALL">All Roles</SelectItem>
            <SelectItem value="SUPER_ADMIN">Super Admin</SelectItem>
            <SelectItem value="ADMIN">Admin</SelectItem>
            <SelectItem value="POS">POS</SelectItem>
            <SelectItem value="KITCHEN">Kitchen</SelectItem>
            <SelectItem value="RIDER">Rider</SelectItem>
            <SelectItem value="KIOSK">Kiosk</SelectItem>
          </SelectContent>
        </Select>
        <Select value={filterStore} onValueChange={setFilterStore}>
          <SelectTrigger>
            <SelectValue placeholder="Filter by store" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="ALL">All Stores</SelectItem>
            {stores.map(store => (
              <SelectItem key={store.id} value={store.id}>{store.name}</SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 mb-6">
        <Card className="bg-gradient-to-r from-blue-50 to-blue-100 border-blue-200">
          <CardContent className="p-4">
            <p className="text-sm text-blue-600 font-medium">Total Users</p>
            <p className="text-2xl font-bold text-blue-800">{users.length}</p>
          </CardContent>
        </Card>
        <Card className="bg-gradient-to-r from-purple-50 to-purple-100 border-purple-200">
          <CardContent className="p-4">
            <p className="text-sm text-purple-600 font-medium">Admins</p>
            <p className="text-2xl font-bold text-purple-800">{users.filter(u => u.role === 'ADMIN').length}</p>
          </CardContent>
        </Card>
        <Card className="bg-gradient-to-r from-green-50 to-green-100 border-green-200">
          <CardContent className="p-4">
            <p className="text-sm text-green-600 font-medium">POS Staff</p>
            <p className="text-2xl font-bold text-green-800">{users.filter(u => u.role === 'POS').length}</p>
          </CardContent>
        </Card>
        <Card className="bg-gradient-to-r from-orange-50 to-orange-100 border-orange-200">
          <CardContent className="p-4">
            <p className="text-sm text-orange-600 font-medium">Kitchen + Riders</p>
            <p className="text-2xl font-bold text-orange-800">{users.filter(u => ['KITCHEN', 'RIDER'].includes(u.role)).length}</p>
          </CardContent>
        </Card>
      </div>

      {/* Users Grid */}
      {loading ? (
        <div className="text-center py-12 text-gray-500">
          <p>Loading users...</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {filteredUsers.map((user) => (
            <Card key={user.id} className="hover:shadow-lg transition-shadow">
              <CardHeader className="pb-3">
                <CardTitle className="text-lg flex items-center justify-between">
                  <div className="flex items-center">
                    <Users className="w-5 h-5 mr-2 text-blue-600" />
                    <span className="truncate max-w-[150px]">{user.name}</span>
                  </div>
                  <span className={`px-2 py-1 rounded text-xs font-bold ${getRoleBadge(user.role)}`}>
                    {user.role}
                  </span>
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-2">
                  <div>
                    <p className="text-xs text-gray-500">Email</p>
                    <p className="text-sm font-medium break-all">{user.email}</p>
                  </div>
                  {user.phone && (
                    <div>
                      <p className="text-xs text-gray-500">Phone</p>
                      <p className="text-sm font-medium">{user.phone}</p>
                    </div>
                  )}
                  {user.store_id && (
                    <div className="flex items-center gap-1 mt-2">
                      <Building2 className="w-3 h-3 text-gray-400" />
                      <p className="text-xs text-gray-600">{getStoreName(user.store_id)}</p>
                    </div>
                  )}
                  <div className="flex gap-2 mt-3 pt-3 border-t">
                    <Button 
                      size="sm" 
                      variant="outline"
                      onClick={() => handleEdit(user)}
                      className="flex-1"
                    >
                      <Edit2 className="w-3 h-3 mr-1" />
                      Edit
                    </Button>
                    <Button 
                      size="sm" 
                      variant="outline"
                      onClick={() => handleResetPassword(user)}
                      className="flex-1"
                    >
                      <Key className="w-3 h-3 mr-1" />
                      Reset
                    </Button>
                    {user.role !== 'SUPER_ADMIN' && (
                      <Button 
                        size="sm" 
                        variant="outline"
                        onClick={() => handleDeleteUser(user)}
                        className="text-red-600 hover:bg-red-50"
                      >
                        <Trash2 className="w-3 h-3" />
                      </Button>
                    )}
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
          
          {filteredUsers.length === 0 && (
            <div className="col-span-full text-center py-12 text-gray-500">
              <Users className="w-12 h-12 mx-auto mb-3 text-gray-400" />
              <p>No users found matching your filters</p>
            </div>
          )}
        </div>
      )}

      {/* Edit User Modal */}
      <Dialog open={showEditModal} onOpenChange={setShowEditModal}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Edit User</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label>Name *</Label>
              <Input
                value={editForm.name}
                onChange={(e) => setEditForm({ ...editForm, name: e.target.value })}
                placeholder="User name"
              />
            </div>
            <div>
              <Label>Email *</Label>
              <Input
                type="email"
                value={editForm.email}
                onChange={(e) => setEditForm({ ...editForm, email: e.target.value })}
                placeholder="user@sspicepizza.com"
              />
            </div>
            <div>
              <Label>Phone</Label>
              <Input
                type="tel"
                value={editForm.phone}
                onChange={(e) => setEditForm({ ...editForm, phone: e.target.value })}
                placeholder="9876543210"
              />
            </div>
            <div className="bg-yellow-50 border border-yellow-200 rounded p-3">
              <p className="text-sm text-yellow-800">
                <strong>Note:</strong> Role and Store cannot be changed. Use "Reset Password" to generate a new password.
              </p>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowEditModal(false)}>Cancel</Button>
            <Button onClick={handleSaveEdit} className="bg-blue-600 hover:bg-blue-700">
              Save Changes
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Create User Modal */}
      <Dialog open={showCreateModal} onOpenChange={setShowCreateModal}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Create New User</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label>Name *</Label>
              <Input
                value={createForm.name}
                onChange={(e) => setCreateForm({ ...createForm, name: e.target.value })}
                placeholder="Full name"
              />
            </div>
            <div>
              <Label>Email *</Label>
              <Input
                type="email"
                value={createForm.email}
                onChange={(e) => setCreateForm({ ...createForm, email: e.target.value })}
                placeholder="user@sspicepizza.com"
              />
            </div>
            <div>
              <Label>Phone</Label>
              <Input
                type="tel"
                value={createForm.phone}
                onChange={(e) => setCreateForm({ ...createForm, phone: e.target.value })}
                placeholder="9876543210"
              />
            </div>
            <div>
              <Label>Password *</Label>
              <Input
                type="password"
                value={createForm.password}
                onChange={(e) => setCreateForm({ ...createForm, password: e.target.value })}
                placeholder="Enter password"
              />
            </div>
            <div>
              <Label>Role *</Label>
              <Select value={createForm.role} onValueChange={(value) => setCreateForm({ ...createForm, role: value })}>
                <SelectTrigger>
                  <SelectValue placeholder="Select role" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="SUPER_ADMIN">Super Admin (Master Access)</SelectItem>
                  <SelectItem value="ADMIN">Admin (Store Manager)</SelectItem>
                  <SelectItem value="POS">POS (Cashier/Order Taker)</SelectItem>
                  <SelectItem value="KITCHEN">Kitchen (KDS Operator)</SelectItem>
                  <SelectItem value="RIDER">Rider (Delivery Person)</SelectItem>
                  <SelectItem value="KIOSK">Kiosk (Self-Service)</SelectItem>
                </SelectContent>
              </Select>
            </div>
            {createForm.role !== 'SUPER_ADMIN' && (
              <div>
                <Label>Store *</Label>
                <Select value={createForm.store_id} onValueChange={(value) => setCreateForm({ ...createForm, store_id: value })}>
                  <SelectTrigger>
                    <SelectValue placeholder="Select store" />
                  </SelectTrigger>
                  <SelectContent>
                    {stores.map(store => (
                      <SelectItem key={store.id} value={store.id}>{store.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}
            <div className="bg-blue-50 border border-blue-200 rounded p-3">
              <p className="text-sm text-blue-800">
                <strong>Roles:</strong><br/>
                • <strong>Super Admin:</strong> Access to all stores<br/>
                • <strong>Admin:</strong> Full store access<br/>
                • <strong>POS:</strong> Order taking & billing<br/>
                • <strong>Kitchen:</strong> KDS & order prep<br/>
                • <strong>Rider:</strong> Deliveries only<br/>
                • <strong>Kiosk:</strong> Customer self-order
              </p>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowCreateModal(false)}>Cancel</Button>
            <Button onClick={handleCreateUser} className="bg-green-600 hover:bg-green-700">
              Create User
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default SuperAdminUserManagement;
